'use client'

import { useMemo, useState } from 'react'
import { ExternalLink, Search } from 'lucide-react'
import { newChirpExperimentalRadios, radioCatalog, vendors } from '@/lib/supported-radios'
import { BROWSER_RADIO_PROFILES } from '@/lib/radio-web'

const experimentalSuffix = ' (experimental)'
const webReadyModels = new Set(
  BROWSER_RADIO_PROFILES.flatMap((profile) =>
    profile.protocol === 'ft60'
      ? ['Yaesu|FT-60', 'Yaesu|FT-60R', 'Yaesu|FT-60 / FT-60R']
      : profile.protocol === 'vx170'
        ? [
            `Yaesu|${profile.model}`,
            ...(profile.model === 'FT-270'
              ? ['Yaesu|FT-270 (use VX-170)']
              : []),
          ]
      : [`${profile.vendor}|${profile.model}`],
  ),
)

function isWebReady(vendor: string, model: string): boolean {
  const cleanModel = model.replace(experimentalSuffix, '').replace(/ \*$/, '').trim()
  return webReadyModels.has(`${vendor}|${cleanModel}`)
}

export function SupportedRadios() {
  const [query, setQuery] = useState('')
  const normalizedQuery = query.trim().toLowerCase()

  const filtered = useMemo(() => {
    if (!normalizedQuery) return vendors
    return vendors
      .map((vendor) => {
        const vendorMatch = vendor.name.toLowerCase().includes(normalizedQuery)
        const models = vendorMatch
          ? vendor.models
          : vendor.models.filter((m) => m.toLowerCase().includes(normalizedQuery))
        return { ...vendor, models }
      })
      .filter((vendor) => vendor.models.length > 0)
  }, [normalizedQuery])

  const visibleExperimentalRadios = useMemo(() => {
    if (!normalizedQuery) return newChirpExperimentalRadios
    return newChirpExperimentalRadios.filter(
      (radio) =>
        radio.vendor.toLowerCase().includes(normalizedQuery) ||
        radio.model.toLowerCase().includes(normalizedQuery),
    )
  }, [normalizedQuery])

  const visibleModelCount = filtered.reduce((total, vendor) => total + vendor.models.length, 0)

  return (
    <section id="radios" className="border-b border-border/60 py-12 md:py-16">
      <div className="mx-auto max-w-7xl px-4">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <span className="font-mono text-sm text-primary">// hardware</span>
            <h2 className="mt-3 text-balance text-3xl font-bold tracking-tight sm:text-4xl">
              Supported radios
            </h2>
            <p className="mt-4 text-pretty leading-relaxed text-muted-foreground">
              Browse the compatible radio catalog: {radioCatalog.totalModels} models across{' '}
              {radioCatalog.totalBrands} brands, including known aliases, compatible clones, and
              experimental NewChirp drivers. Every listed radio is supported by the desktop
              application. A <span className="font-semibold text-primary">desktop + web</span>{' '}
              badge means it is also available in the browser Web Programmer. Search for a brand
              or model below.
            </p>
          </div>

          <div className="relative w-full md:w-72">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search e.g. UV-5R"
              className="w-full rounded-lg border border-border bg-card py-2.5 pl-9 pr-3 font-mono text-sm outline-none ring-primary/40 transition focus:border-primary/50 focus:ring-2"
              aria-label="Search supported radios"
            />
          </div>
        </div>

        <div className="mt-8 overflow-hidden rounded-xl border border-[#9b5b18]/30 bg-card">
          <div className="flex flex-col gap-3 border-b border-[#9b5b18]/20 bg-[#9b5b18]/5 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="font-semibold text-foreground">New Experimental Radio Support</h3>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                Drivers developed and maintained directly by NewChirp. These are also listed under
                their manufacturers below while hardware validation continues.
              </p>
            </div>
            <span className="self-start rounded-md border border-[#9b5b18]/25 bg-[#9b5b18]/10 px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-wide text-[#9b5b18] sm:self-auto">
              {newChirpExperimentalRadios.length}{' '}
              {newChirpExperimentalRadios.length === 1 ? 'radio' : 'radios'}
            </span>
          </div>
          <div className="grid gap-3 p-5 sm:grid-cols-2 lg:grid-cols-3">
            {visibleExperimentalRadios.length > 0 ? (
              visibleExperimentalRadios.map((radio) => (
                <div
                  key={`${radio.vendor}-${radio.model}`}
                  className="rounded-lg border border-border bg-background px-4 py-3"
                >
                  <p className="text-xs text-muted-foreground">{radio.vendor}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <span className="font-mono text-sm font-semibold text-foreground">
                      {radio.model}
                    </span>
                    <span
                      className="rounded bg-[#9b5b18]/10 px-1 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wide text-[#9b5b18]"
                      title="NewChirp-maintained driver; test results are still being collected"
                    >
                      experimental
                    </span>
                    {isWebReady(radio.vendor, radio.model) && (
                      <span
                        className="rounded bg-primary/10 px-1 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wide text-primary"
                        title="Supported by desktop NewChirp and the browser Web Programmer beta"
                      >
                        desktop + web
                      </span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className="col-span-full py-2 text-sm text-muted-foreground">
                No experimental radios match the current search.
              </p>
            )}
          </div>
        </div>

        <div className="mt-8 flex flex-col gap-3 rounded-xl border border-border bg-card px-5 py-4 text-sm sm:flex-row sm:items-center sm:justify-between">
          <p className="text-muted-foreground" aria-live="polite">
            Showing <span className="font-semibold text-foreground">{visibleModelCount}</span>{' '}
            {visibleModelCount === 1 ? 'model' : 'models'} across{' '}
            <span className="font-semibold text-foreground">{filtered.length}</span>{' '}
            {filtered.length === 1 ? 'brand' : 'brands'}
          </p>
          <a
            href={radioCatalog.sourceUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 font-medium text-primary transition-opacity hover:opacity-75"
          >
            Official CHIRP list
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>

        {filtered.length === 0 ? (
          <p className="mt-12 rounded-xl border border-dashed border-border bg-card/50 p-10 text-center text-muted-foreground">
            No matches for{' '}
            <span className="font-mono text-foreground">&quot;{query}&quot;</span> in the current CHIRP
            catalog.
          </p>
        ) : (
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((vendor) => (
              <div key={vendor.name} className="rounded-xl border border-border bg-card p-5">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">{vendor.name}</h3>
                  <span className="font-mono text-xs text-muted-foreground">
                    {vendor.models.length}
                  </span>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {vendor.models.map((model) => {
                    const webReady = isWebReady(vendor.name, model)
                    return (
                      <span
                        key={model}
                        className="rounded-md border border-border bg-background px-2 py-1 font-mono text-xs leading-relaxed text-muted-foreground"
                      >
                        {model.endsWith(experimentalSuffix) ? (
                          <>
                            {model.slice(0, -experimentalSuffix.length)}{' '}
                            <span
                              className="ml-0.5 rounded bg-[#9b5b18]/10 px-1 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#9b5b18]"
                              title="NewChirp-maintained driver; test results are still being collected"
                            >
                              experimental
                            </span>
                          </>
                        ) : model.endsWith(' *') ? (
                          <>
                            {model.slice(0, -2)}{' '}
                            <span
                              className="font-bold text-[#9b5b18]"
                              title="Legacy CHIRP support; not yet validated in CHIRP-next"
                              aria-label="legacy support"
                            >
                              *
                            </span>
                          </>
                        ) : (
                          model
                        )}
                        {webReady && (
                          <span
                            className="ml-1 rounded bg-primary/10 px-1 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary"
                            title="Supported by desktop NewChirp and the browser Web Programmer beta"
                          >
                            desktop + web
                          </span>
                        )}
                      </span>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 rounded-xl border border-border bg-secondary/70 p-5 text-sm leading-relaxed text-muted-foreground">
          <p>
            <span className="font-semibold text-foreground">About this list:</span> Entries that say
            “use” identify compatible CHIRP drivers for rebranded or related radios. An asterisk (*)
            marks legacy CHIRP support that has not yet been validated in CHIRP-next. Models marked
            “experimental” are maintained by NewChirp outside upstream CHIRP and are still being
            validated with hardware. All catalog entries are available in desktop NewChirp.
            Models marked “desktop + web” are also available in the{' '}
            <a href="/programmer" className="font-semibold text-primary hover:underline">
              browser Web Programmer beta
            </a>
            . Feature support varies by radio. Catalog synchronized from CHIRP on{' '}
            {radioCatalog.syncedOn}.
          </p>
        </div>
      </div>
    </section>
  )
}
